import React from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Megaphone, ShieldOff } from 'lucide-react';
import { stateAbbreviations } from 'constants/enum';
import LoadingContent from 'components/LoadingContent/LoadingContent';
import CarrierLucideIcon from 'components/CarrierLucideIcon/CarrierLucideIcon';
import {
  getCarrierDisplayName,
  getConfirmedStates,
  getPendingStates
} from 'helpers/appointments/appointments';
import {
  OEP_UPDATE_ICON_COLOR,
  OEP_UPDATE_ICON_DESCRIPTION
} from 'helpers/oepUpdates';
import { cn } from 'lib/utils';

// Match CarrierCard.module.scss: font-size calc($font-size-subtitle - 3px), calc($font-size-body - 2px)
const TITLE_FONT_SIZE = 'calc(1.33rem - 3px)';
const STATE_FONT_SIZE = 'calc(1rem - 2px)';
const CARRIER_ICON_SIZE = 18;

// Fallbacks so colors apply even when theme CSS vars aren't loaded (e.g. Health section)
const COLORS = {
  greyLightest: 'var(--color-grey-lightest, #f6f6f6)',
  secondary: 'var(--color-secondary, #93d500)',
  secondaryLight: 'var(--color-secondary-light, #e2f7b4)',
  primaryEmphasisLight: 'var(--color-primary-emphasis-light, #39aaff)',
  grey: 'var(--color-grey, #656464)',
  pending: 'var(--color-pending, #f3b86b)',
  semanticDanger: 'var(--color-semantic-danger, #f22344)',
  blue: 'var(--color-blue, #3C6EB2)'
};

export default function CarrierCard({
  active,
  setActive,
  name,
  carrierIcon,
  carrierIconColor,
  carrierActionableAppts = [],
  agencyDisabled = false,
  showOepIcon = false
}) {
  const user = useSelector(state => state.user.zohoData);
  const {
    isSendingRequestAppointments,
    requestedCarrierName,
    requestedAppointmentsByCarrier
  } = useSelector(state => state.appointments);

  const carrierReqAppts = requestedAppointmentsByCarrier[name] ?? [];
  const confirmedStates = getConfirmedStates(name, user);
  const allPendingStates = getPendingStates(carrierReqAppts, confirmedStates);
  const actionablePendingStates =
    carrierActionableAppts.length > 0
      ? getPendingStates(carrierActionableAppts, confirmedStates)
      : [];
  const actionableSet = new Set(actionablePendingStates);

  const sortedPendingStates = [...allPendingStates].sort((a, b) => {
    const aIsActionable = actionableSet.has(a);
    const bIsActionable = actionableSet.has(b);
    if (aIsActionable === bIsActionable) return 0;
    return aIsActionable ? -1 : 1;
  });

  const notRequested =
    confirmedStates.length === 0 && allPendingStates.length === 0;
  const isActive = active === name;

  const getCardStyle = () => {
    const base = { font: 'inherit', transition: 'filter 100ms' };
    if (isActive) return { ...base, backgroundColor: COLORS.secondary };
    return { ...base, backgroundColor: COLORS.greyLightest };
  };

  const confirmedColor = isActive ? '#fff' : COLORS.secondary;
  const pendingColor = isActive ? '#ffc800' : COLORS.pending;
  const actionableColor = COLORS.semanticDanger;

  return (
    <button
      type='button'
      className={cn(
        'h-[120px] w-full min-w-0 cursor-pointer rounded-[5px] border-0 text-left hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset px-2 pt-4 pb-2 flex flex-col gap-2'
      )}
      style={getCardStyle()}
      onClick={isActive ? undefined : () => setActive(name)}
    >
      {isSendingRequestAppointments && requestedCarrierName === name ? (
        <LoadingContent />
      ) : (
        <>
          <h2
            className='flex min-w-0 shrink-0 items-center justify-center gap-2 overflow-hidden font-primary font-semibold leading-none m-0'
            style={{
              fontSize: TITLE_FONT_SIZE,
              color: isActive ? '#fff' : COLORS.grey
            }}
          >
            <div className='flex shrink-0 items-center gap-1.5'>
              {agencyDisabled && (
                <ShieldOff
                  size={CARRIER_ICON_SIZE}
                  className='shrink-0 text-semantic-danger'
                  aria-hidden
                />
              )}
              <CarrierLucideIcon
                name={carrierIcon}
                color={carrierIconColor}
                size={CARRIER_ICON_SIZE}
                whiteIcon={isActive}
              />
              {showOepIcon && (
                <span
                  className='flex shrink-0 items-center'
                  title={OEP_UPDATE_ICON_DESCRIPTION}
                >
                  <Megaphone
                    size={CARRIER_ICON_SIZE}
                    style={{ color: isActive ? '#fff' : OEP_UPDATE_ICON_COLOR }}
                    aria-label={OEP_UPDATE_ICON_DESCRIPTION}
                  />
                </span>
              )}
            </div>
            <span className='block min-w-0 truncate'>
              {getCarrierDisplayName(name)}
            </span>
          </h2>
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col justify-between overflow-hidden font-medium'
            )}
            style={{ color: isActive ? '#fff' : COLORS.grey }}
          >
            {!notRequested && (
              <div
                className='flex flex-wrap overflow-y-auto show-scrollbar font-primary'
                style={{
                  fontSize: STATE_FONT_SIZE
                }}
              >
                    {confirmedStates.length > 0 &&
                      confirmedStates.map((state, index) => (
                        <span
                          style={{ color: confirmedColor }}
                          key={state}
                          className='whitespace-nowrap'
                        >
                          {index === confirmedStates.length - 1 &&
                          !sortedPendingStates.length > 0
                            ? `${stateAbbreviations[state]}\u00A0`
                            : `${stateAbbreviations[state]},\u00A0`}
                        </span>
                      ))}
                    {sortedPendingStates.length > 0 &&
                      sortedPendingStates.map((state, i) => (
                        <span
                          key={state}
                          className='whitespace-nowrap'
                          style={{
                            color:
                              !isActive &&
                              actionablePendingStates.includes(state)
                                ? actionableColor
                                : pendingColor
                          }}
                        >
                          {i === sortedPendingStates.length - 1
                            ? `${stateAbbreviations[state]}\u00A0`
                            : `${stateAbbreviations[state]},\u00A0`}
                        </span>
                      ))}
                  </div>
                )}
          </div>
        </>
      )}
    </button>
  );
}

CarrierCard.propTypes = {
  name: propTypes.string,
  carrierIcon: propTypes.string,
  carrierIconColor: propTypes.string,
  active: propTypes.string,
  setActive: propTypes.func,
  carrierActionableAppts: propTypes.array,
  agencyDisabled: propTypes.bool,
  showOepIcon: propTypes.bool
};
